import { useState, useEffect } from "react";
import axiosInstance from "../../redux/helpers/axios";
import inventory from "../../redux/helpers/inventory";

const ACCENT_FROM = "#E11D74";
const ACCENT_TO = "#EF4444";
const inputClass =
  "mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2";

const EMPTY_FORM = {
  campaignSegmentId: "",
  campaignCategoryId: "",
  productId: "",
  productTitle: "",
  productImg: "",
  productUrl: "",
  percentage: "",
  fxRate: "",
  moq: "",
  rate: "",
};

// campaignId comes from the Products page's campaign selector; segment/category are still
// chosen here since a campaign can have many of each.
export default function CreateCampaignProductForm({
  campaignId,
  onCreated,
  onCancel,
}) {
  const [segments, setSegments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState({ segments: true, categories: false });
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState({ loading: false, error: null });
  const [product, setProduct] = useState(null);
  const [ldcMeta, setLdcMeta] = useState(null);

  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const res = await axiosInstance.get(
          `/admin/campaign/segment/get-by-campaign/${campaignId}`,
        );
        setSegments(res.data.segments || []);
      } catch (err) {
        setStatus((prev) => ({ ...prev, error: "Failed to load segments" }));
      } finally {
        setLoading((prev) => ({ ...prev, segments: false }));
      }
    };
    fetchSegments();
  }, [campaignId]);

  useEffect(() => {
    if (!form.campaignSegmentId) {
      setCategories([]);
      return;
    }
    const fetchCategories = async () => {
      setLoading((prev) => ({ ...prev, categories: true }));
      try {
        const res = await axiosInstance.get(
          `/admin/campaign/category/get-by-segment/${form.campaignSegmentId}`,
        );
        setCategories(res.data.categories || []);
      } catch (err) {
        setStatus((prev) => ({ ...prev, error: "Failed to load categories" }));
      } finally {
        setLoading((prev) => ({ ...prev, categories: false }));
      }
    };
    fetchCategories();
  }, [form.campaignSegmentId]);

  const handleSegmentChange = (e) => {
    setForm((prev) => ({
      ...prev,
      campaignSegmentId: e.target.value,
      campaignCategoryId: "",
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, error: null });

    try {
      const res = await axiosInstance.post(
        "/admin/campaign/segment/category/product/create",
        {
          productId: form.productId,
          productTitle: form.productTitle,
          productImg: form.productImg,
          productUrl: form.productUrl,
          fxRate: form.fxRate ? Number(form.fxRate) : null,
          moq: form.moq ? Number(form.moq) : null,
          rate: form.rate ? Number(form.rate) : null,
          campaignCategoryId: form.campaignCategoryId,
          product,
          ldcMeta,
          percentage: form.percentage ? Number(form.percentage) : 0,
        },
      );
      onCreated(res?.data?.product);
    } catch (err) {
      setStatus({
        loading: false,
        error:
          err?.response?.data?.error ||
          err?.response?.data?.msg ||
          "Failed to create product",
      });
    }
  };

  const handleFetchProduct = async () => {
    if (!form.productId) {
      setStatus({ loading: false, error: "Product ID is required" });
      return;
    }
    setStatus({ loading: true, error: null });

    try {
      const res = await inventory.get(`/product/find/${form.productId}`);
      const product = res.data.product;
      if (!product) {
        setStatus({ loading: false, error: "Product not found" });
        return;
      }
      setProduct(product);
      setForm((prev) => ({
        ...prev,
        productTitle: product?.title || "",
        productImg: product?.main_imgs[0] || "",
        productUrl: product?.product_url || "",
      }));
    } catch (err) {
      setStatus({
        loading: false,
        error:
          err?.response?.data?.error ||
          err?.response?.data?.msg ||
          "Failed to fetch product",
      });
    }
    setStatus((prev) => ({ ...prev, loading: false }));
  };

  const handleFetchLDC = async () => {
    if (!form.productId) {
      setStatus({ loading: false, error: "Product ID is required" });
      return;
    }

    setStatus({ loading: true, error: null });

    try {
      const res = await inventory.get(
        `/shipping/calculate/${form.productId}?total_quantity=1&province=Guangdong`,
      );
      // Do something with the LDC data, e.g., update the form
      const { shipping } = res.data;
      setLdcMeta(shipping);
    } catch (err) {
      setStatus({
        loading: false,
        error:
          err?.response?.data?.error ||
          err?.response?.data?.msg ||
          "Failed to fetch LDC",
      });
    } finally {
      setStatus((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Segment
        </label>
        <select
          required
          value={form.campaignSegmentId}
          onChange={handleSegmentChange}
          disabled={loading.segments}
          className={`${inputClass} bg-white`}
          style={{ "--tw-ring-color": ACCENT_FROM }}
        >
          <option value="">
            {loading.segments ? "Loading segments..." : "Select a segment"}
          </option>
          {segments.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Category
        </label>
        <select
          name="campaignCategoryId"
          required
          value={form.campaignCategoryId}
          onChange={handleChange}
          disabled={!form.campaignSegmentId || loading.categories}
          className={`${inputClass} bg-white disabled:bg-gray-50`}
          style={{ "--tw-ring-color": ACCENT_FROM }}
        >
          <option value="">
            {!form.campaignSegmentId
              ? "Select a segment first"
              : loading.categories
                ? "Loading categories..."
                : "Select a category"}
          </option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <hr className="border-gray-200" />

      <div className="flex flex-col gap-2">
        <label className="block text-sm font-medium text-gray-700">
          Product ID
        </label>
        <input
          type="text"
          name="productId"
          required
          value={form.productId}
          onChange={handleChange}
          className={inputClass}
          style={{ "--tw-ring-color": ACCENT_FROM }}
        />

        <div className="flex gap-2">
          <button
            onClick={() => handleFetchProduct()}
            disabled={status.loading}
            className="flex-1 rounded-md border border-gray-300 py-2.5 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
            style={{ marginTop: "0.5rem" }}
          >
            Fetch product details
          </button>

          <button
            onClick={() => handleFetchLDC()}
            disabled={status.loading}
            className="flex-1 rounded-md border border-gray-300 py-2.5 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
            style={{ marginTop: "0.5rem" }}
          >
            Fetch LDC
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Product title
        </label>
        <input
          type="text"
          name="productTitle"
          required
          value={form.productTitle}
          onChange={handleChange}
          className={inputClass}
          style={{ "--tw-ring-color": ACCENT_FROM }}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Product image URL
        </label>
        <input
          type="text"
          name="productImg"
          value={form.productImg}
          onChange={handleChange}
          placeholder="https://..."
          className={inputClass}
          style={{ "--tw-ring-color": ACCENT_FROM }}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Product URL
        </label>
        <input
          type="text"
          name="productUrl"
          value={form.productUrl}
          onChange={handleChange}
          placeholder="https://..."
          className={inputClass}
          style={{ "--tw-ring-color": ACCENT_FROM }}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Discount percentage
        </label>
        <input
          type="number"
          step="0.01"
          name="percentage"
          value={form.percentage}
          onChange={handleChange}
          className={inputClass}
          style={{ "--tw-ring-color": ACCENT_FROM }}
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            FX rate
          </label>
          <input
            type="number"
            step="0.0001"
            name="fxRate"
            value={form.fxRate}
            onChange={handleChange}
            className={inputClass}
            style={{ "--tw-ring-color": ACCENT_FROM }}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">MOQ</label>
          <input
            type="number"
            name="moq"
            value={form.moq}
            onChange={handleChange}
            className={inputClass}
            style={{ "--tw-ring-color": ACCENT_FROM }}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Rate
          </label>
          <input
            type="number"
            step="0.1"
            name="rate"
            value={form.rate}
            onChange={handleChange}
            className={inputClass}
            style={{ "--tw-ring-color": ACCENT_FROM }}
          />
        </div>
      </div>

      {status.error && (
        <p className="text-sm text-red-600" role="alert">
          {status.error}
        </p>
      )}

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 rounded-md border border-gray-300 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={status.loading}
          className="flex-1 rounded-md py-2.5 text-sm font-medium text-white transition disabled:opacity-60"
          style={{
            background: `linear-gradient(90deg, ${ACCENT_FROM}, ${ACCENT_TO})`,
          }}
        >
          {status.loading ? "Adding..." : "Add product"}
        </button>
      </div>
    </form>
  );
}
